import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';

import { db } from '../db/db.ts';
import { metaRepository } from '../db/repositories/meta.ts';
import { onSync } from './engine.ts';

/**
 * What the header indicator and the status panel both read.
 *
 * The rule behind all of it, from hazard H2 in COMPLIANCE.md: a trainee must
 * always be able to tell whether their work is safe, and **must never be
 * falsely reassured**. Every state here errs towards saying "not yet" rather
 * than "done".
 */

export type SyncState = 'synced' | 'pending' | 'offline' | 'problem';

/** Anything queued this long is no longer a blip. The H2 control. */
export const STALE_PENDING_MS = 24 * 60 * 60 * 1000;

export type SyncStatus = {
  state: SyncState;
  pending: number;
  failed: number;
  /** When a sync last completed without error, or null if never. */
  lastSyncedAt: Date | null;
  /** Age of the oldest thing still waiting, in milliseconds. */
  oldestPendingMs: number | null;
  /** How long ago the last successful sync was, or null if there has not been one. */
  lastSyncedAgoMs: number | null;
  stale: boolean;
  online: boolean;
};

/** How often the clock is re-read, so "an hour ago" becomes "2 hours ago". */
const CLOCK_TICK_MS = 60_000;

export function useSyncStatus(userId: string | null): SyncStatus {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [syncTick, setSyncTick] = useState(0);

  /*
   * The clock lives in state rather than being read while rendering. Two
   * reasons, and the second is the one that matters: a render must be pure, and
   * ages shown on screen have to move on their own — the 24-hour staleness
   * warning is useless if it only appears when something else happens to
   * re-render the header.
   */
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const goOnline = (): void => setOnline(true);
    const goOffline = (): void => setOnline(false);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Re-read after every sync, so the panel's "last synced" is not stale.
  useEffect(() => onSync(() => setSyncTick((tick) => tick + 1)), []);

  const data = useLiveQuery(async () => {
    if (userId === null) {
      return { pending: 0, failed: 0, oldest: null as Date | null, lastSyncedAt: null };
    }

    const queued = await db.outbox.toArray();
    const mine = queued.filter((item) => item.userId === userId);
    const pending = mine.filter((item) => item.status === 'pending');
    const failed = mine.filter((item) => item.status === 'failed');

    const oldest = mine.reduce<Date | null>(
      (earliest, item) =>
        earliest === null || item.createdAt.getTime() < earliest.getTime()
          ? item.createdAt
          : earliest,
      null,
    );

    const lastPulled = await metaRepository.getLastPulledAt();

    return {
      pending: pending.length,
      failed: failed.length,
      oldest,
      lastSyncedAt: lastPulled === undefined ? null : new Date(lastPulled),
    };
  }, [userId, syncTick]);

  const pending = data?.pending ?? 0;
  const failed = data?.failed ?? 0;
  const oldestPendingMs = data?.oldest == null ? null : now - data.oldest.getTime();
  const stale = oldestPendingMs !== null && oldestPendingMs > STALE_PENDING_MS;

  return {
    state: resolveState({ online, pending, failed, stale }),
    pending,
    failed,
    lastSyncedAt: data?.lastSyncedAt ?? null,
    oldestPendingMs,
    lastSyncedAgoMs:
      data?.lastSyncedAt == null ? null : Math.max(0, now - data.lastSyncedAt.getTime()),
    stale,
    online,
  };
}

/**
 * Worst news first. Being offline with nothing queued is not worth mentioning;
 * anything stuck or failed outranks everything else, because that is the state
 * a trainee needs to act on.
 */
export function resolveState(input: {
  online: boolean;
  pending: number;
  failed: number;
  stale: boolean;
}): SyncState {
  if (input.failed > 0 || input.stale) return 'problem';
  if (!input.online && input.pending > 0) return 'offline';
  if (input.pending > 0) return 'pending';
  return 'synced';
}

/** "3 minutes ago", "yesterday" — for the panel, which has room for words. */
export function describeAge(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes === 1) return 'a minute ago';
  if (minutes < 60) return `${String(minutes)} minutes ago`;

  const hours = Math.floor(minutes / 60);
  if (hours === 1) return 'an hour ago';
  if (hours < 24) return `${String(hours)} hours ago`;

  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${String(days)} days ago`;
}
